import React, { useContext } from "react";
import { DatePicker } from "react-date-picker";
import styled from "styled-components";

import {
  fromAWSDate,
  locale,
  toAWSDate,
} from "@examspark/common/dist/utils/coursePlanner";

import { FormContext } from "../../contexts/FormContext";
import constants from "../../utils/constants";
import { HybridField, useHybridState } from "../../utils/useHybridComponent";
import { IconCalendarLine } from "../icons";

export const StyledDateInput = styled.div<Props>`
  ${(props) =>
    props.stretch &&
    `
    .react-date-picker{
      width: 100%;
    }
  `}

  .react-calendar {
    padding: 0.5rem;
    box-shadow: ${constants.shadow};
    border-radius: 0.3rem;
    border-color: #bbb;
    margin: 0.7rem 0;
    font-family: inherit;
  }
  .react-date-picker__wrapper {
    border-radius: 0.3rem;
    border-color: #ddd;
    height: 3rem;
    background-color: #fff;
  }
  .react-calendar__tile--now {
    background-color: hsl(115, 51%, 70%);
  }
  .react-calendar__tile--now:hover {
    background-color: hsl(115, 51%, 65%);
  }
  .react-calendar__tile--active.react-calendar__tile--now {
    background-color: hsl(115, 51%, 60%);
  }
  .react-calendar__tile--active.react-calendar__tile--now:hover {
    background-color: hsl(115, 51%, 55%);
  }

  .react-date-picker__inputGroup {
    padding: 0.5rem;
  }

  .react-date-picker__calendar {
    width: 27rem;
  }
  .react-date-picker__calendar-button {
    margin: 0.3rem;
    padding: 0.2rem;
    border-radius: 0.3rem;
    transition: background-color 0.2s;

    svg {
      fill: #555;
    }
  }
  .react-date-picker__calendar-button:hover,
  .react-date-picker__calendar-button:focus-visible {
    background-color: #e2e2e2;
  }

  .react-date-picker--open {
    .react-date-picker__wrapper {
      border-color: hsl(203, 80%, 45%);
      box-shadow: 0 0 0px 0px hsla(203, 80%, 45%, 0.3);
      animation: mymove 0.3s 1;
      animation-fill-mode: forwards;
    }
  }

  @keyframes mymove {
    100% {
      box-shadow: 0 0 0px 3px hsla(203, 80%, 45%, 0.3);
    }
  }

  input {
    outline: none;
    font-family: inherit;
    font-size: inherit;
  }
  button {
    outline: none;
    font-family: inherit;
    font-size: inherit;
  }

  .react-calendar__tile {
    border-radius: 0.3rem;
  }

  .react-date-picker__calendar {
    border-radius: 0.3rem;
  }

  .react-calendar__navigation {
    height: 35px;
  }
  .react-calendar__navigation button {
    min-width: 35px;
  }
  .react-calendar__navigation__label:disabled {
    color: #000;
    background-color: #fff;
  }
`;

export const convertUtcMidnightToLocalMidnight = (date: Date) => {
  // ensure date is midnight UTC time
  if (date.getUTCMinutes() + date.getUTCHours() + date.getUTCMinutes() !== 0) {
    console.error(
      "DateInput: date is not midnight UTC time. This will probably cause problems.",
    );
  }

  // mm/dd/yyyy in UTC timezone
  const utc = locale.format(date);

  const newDate = new Date(utc);
  return newDate;
};

type Props = HybridField<{ date: Date }> & {
  stretch?: boolean;
  disabled?: boolean;
};
const DateInput: React.FC<React.PropsWithChildren<Props>> = ({
  date: initialDate,
  onDateChange,
  stretch,
  disabled,
}) => {
  const [date, setDate] = useHybridState({
    value: initialDate,
    onValueChange: onDateChange,
    initialValue: new Date(),
  });

  const form = useContext(FormContext);

  return (
    <StyledDateInput stretch={stretch}>
      <DatePicker
        showFixedNumberOfWeeks
        disabled={disabled ?? form.disabled}
        calendarIcon={<IconCalendarLine height="16" />}
        // @ts-expect-error our types are bad
        clearIcon={null}
        locale="en-US"
        minDetail="year"
        // DatePicker only really understands Date objects that are Midnight Local Time
        value={date}
        // @ts-expect-error our types are bad
        onChange={(value: Date) => {
          setDate(value);
        }}
      />
    </StyledDateInput>
  );
};

export default DateInput;

type DateInputAWSProps = HybridField<{ date: string }> & {
  stretch?: boolean;
  disabled?: boolean;

  minDate?: string;
  maxDate?: string;
  // @ts-expect-error invalid props
  tileDisabled?: (date: CalendarTileProperties) => boolean;
};

export const awsDateToDatePickerValue = (stringDate: string | null) => {
  return stringDate != null
    ? convertUtcMidnightToLocalMidnight(fromAWSDate(stringDate))
    : null;
};

/**
 * AwsDateInput is used to mediate (time-agnostic) date values between client
 * and server. These dates are stored in the DB as `2024-01-01Z` to indicate
 * they are meant to represent midnight of the selected date value.
 *
 * `toAWSDate` and `fromAWSDate` are used to convert between the AWS date format
 * and the JS Date object format.
 *
 * Aside: This component is really tricky to understand to someone initially
 * looking to use this component (why is `unwrapSetStateAction` needed?) and
 * could probably use some clarity refactors at some point.
 *
 * Example:
 *
 * ```tsx
 * <AwsDateInput
 *  disabled={!props.value.disableAccessOnOrAfterEnabled}
 *  date={
 *    props.value.disableAccessOnOrAfter
 *      ? props.value.disableAccessOnOrAfter
 *      : toAWSDate(new Date())
 *  }
 *  onDateChange={(value) => {
 *    console.log(value);
 *    props.onChange?.({
 *      ...props.value,
 *      disableAccessOnOrAfter: unwrapSetStateAction(
 *        props.value.disableAccessOnOrAfter
 *          ? props.value.disableAccessOnOrAfter
 *          : toAWSDate(new Date()),
 *        value
 *      ),
 *    });
 *  }}
 * />
 * ```
 */
export const AwsDateInput: React.FC<
  React.PropsWithChildren<DateInputAWSProps>
> = ({
  date: initialDate,
  onDateChange,
  stretch,
  disabled,
  minDate: dateRangeStart,
  maxDate: dateRangeEnd,
  tileDisabled,
}) => {
  const [date, setDate] = useHybridState({
    value: initialDate,
    onValueChange: onDateChange,
    initialValue: toAWSDate(new Date()),
  });

  const form = useContext(FormContext);

  // AWS Dates are midnight UTC but the DatePicker component only really
  // understands midnight local time
  const localDate = convertUtcMidnightToLocalMidnight(fromAWSDate(date));

  const minDate = dateRangeStart
    ? convertUtcMidnightToLocalMidnight(fromAWSDate(dateRangeStart))
    : undefined;
  const maxDate = dateRangeEnd
    ? convertUtcMidnightToLocalMidnight(fromAWSDate(dateRangeEnd))
    : undefined;

  return (
    <StyledDateInput stretch={stretch}>
      <DatePicker
        disabled={disabled ?? form.disabled}
        calendarIcon={<IconCalendarLine height="16" />}
        // @ts-expect-error our types are bad
        clearIcon={null}
        locale="en-US"
        minDetail="year"
        showFixedNumberOfWeeks
        // DatePicker only really understands Date objects that are Midnight Local Time
        value={localDate}
        // @ts-expect-error our types are bad
        onChange={(value: Date) => {
          //toAWSDate already (brutally) converts local to utc
          setDate(toAWSDate(value));
        }}
        tileDisabled={tileDisabled}
        minDate={minDate}
        maxDate={maxDate}
      />
    </StyledDateInput>
  );
};
